import { useMemo } from "react";
import Dropdown from "../../atoms/Dropdown";
import {
  FormPayloadV2,
  sessionFieldsV2,
  PRIMARY_DRUG_NIVOLUMAB,
  PRIMARY_DRUG_TUCATINIB,
} from "../../data/drugOptionsV2";
import Input from "../../atoms/Input/Input";
import { Radio } from "antd";
import Textarea from "../../atoms/TextArea/Textarea";
import useTherapyDrugs from "../../hooks/useTherapyDrugs";
import Spinner from "../../atoms/Spinner/Spinner";

interface CycleOrSessionDetailsV2Props {
  formData: FormPayloadV2;
  setFormData: (formData: FormPayloadV2) => void;
}

const CycleOrSessionDetailsV2 = ({
  formData,
  setFormData,
}: CycleOrSessionDetailsV2Props) => {
  // Fetch therapy drugs from API
  const { getCancerTypesForPrimary, isLoading: loadingDrugs } = useTherapyDrugs();

  // Get cancer types for selected primary drug from API
  const cancerTypesForPrimaryDrug = useMemo(() => {
    if (!formData.primaryDrug) return [];
    return getCancerTypesForPrimary(formData.primaryDrug);
  }, [formData.primaryDrug, getCancerTypesForPrimary]);

  // Get therapies for selected cancer type
  const therapyOptions = useMemo(() => {
    if (!formData.cancerType) return [];
    const selectedCancerType = cancerTypesForPrimaryDrug.find(
      (cancerType) => cancerType.value === formData.cancerType
    );
    return selectedCancerType?.therapies || [];
  }, [cancerTypesForPrimaryDrug, formData.cancerType]);

  // Get indications for selected therapy
  const indicationOptions = useMemo(() => {
    if (!formData.therapy) return [];
    const selectedTherapy = therapyOptions.find(
      (therapy) => therapy.value === formData.therapy
    );
    return selectedTherapy?.indications || [];
  }, [therapyOptions, formData.therapy]);

  // Handle Therapy Change
  const handleTherapyChange = (e: any) => {
    const value = e.target.value;
    setFormData({ ...formData, therapy: value, indication: "" });
  };

  // Handle Indication Change
  const handleIndicationChange = (e: any) => {
    const value = e.target.value;
    setFormData({ ...formData, indication: value });
  };

  // Handle Generic Field Change
  const handleChange = (e: any, name: string) => {
    setFormData({ ...formData, [name]: e.target.value });
  };

  // Handle Brain Mets Change
  const handleBrainMetsChange = (e: any) => {
    setFormData({ ...formData, brain_mets: parseInt(e.target.value) as 0 | 1 });
  };

  // Check if primary drug is Nivolumab
  const isNivolumab = useMemo(() => {
    return formData.primaryDrug === PRIMARY_DRUG_NIVOLUMAB;
  }, [formData.primaryDrug]);

  // Check if primary drug is Tucatinib
  const isTucatinib = useMemo(() => {
    return formData.primaryDrug === PRIMARY_DRUG_TUCATINIB;
  }, [formData.primaryDrug]);

  // Filter form fields based on primary drug
  const formFields = useMemo(() => {
    return sessionFieldsV2.filter((field) => {
      if (field.showForPrimaryDrug) {
        return field.showForPrimaryDrug.includes(formData.primaryDrug || "");
      }
      return true;
    });
  }, [formData.primaryDrug]);

  return (
    <>
      {/* Therapy and Indication Selection */}
      <div className="grid grid-cols-3 items-end gap-4 mt-4 w-full">
        {/* Therapy/Approval Dropdown */}
        {formData.cancerType && therapyOptions.length > 0 && (
          <Dropdown
            options={therapyOptions}
            onChange={handleTherapyChange}
            value={formData.therapy || ""}
            label="Select Approval/Therapy"
            placeholder="Select Therapy"
          />
        )}

        {/* Indication Dropdown */}
        {formData.therapy && indicationOptions.length > 0 && (
          <Dropdown
            options={indicationOptions}
            onChange={handleIndicationChange}
            value={formData.indication || ""}
            label="Select Indication"
            placeholder="Select Indication"
          />
        )}
      </div>

      {/* Dynamic Form Fields */}
      <div className="grid grid-cols-3 items-end gap-4 mt-4 w-full">
        {formFields
          .filter((field) => field.type !== "textarea")
          .map((field, idx) => (
            <div key={idx} className="flex flex-col flex-1">
              <Input
                id={field.name}
                label={field.label}
                placeholder={field.placeholder || ""}
                type={field.type}
                name={field.name}
                value={formData[field.name as keyof FormPayloadV2] || ""}
                onChange={(e: any) => handleChange(e, field.name)}
                min={field.min}
                disabled={field.disabled}
                wrapperClassName="w-full"
              />
            </div>
          ))}
      </div>

      {/* Notes Field - Separate Row */}
      {formFields
        .filter((field) => field.type === "textarea")
        .map((field, idx) => (
          <div key={idx} className="w-full mt-4">
            <Textarea
              label={field.label}
              name={field.name}
              id={field.name}
              rows={4}
              value={String(formData[field.name as keyof FormPayloadV2] || "")}
              onChange={(e: any) => handleChange(e, field.name)}
              placeholder={field.placeholder}
            />
          </div>
        ))}

      {/* Brain Mets for Tucatinib */}
      {isTucatinib && (
        <div className="w-full mt-4 fld">
          <p className="fld-label">Brain Mets</p>
          <Radio.Group
            onChange={handleBrainMetsChange}
            value={String(formData.brain_mets || 0)}
            className="custom-radio"
          >
            <Radio value="1">Yes</Radio>
            <Radio value="0">No</Radio>
          </Radio.Group>
        </div>
      )}
    </>
  );
};

export default CycleOrSessionDetailsV2;
